import { FormlyFieldConfig } from '@ngx-formly/core';

import { Table as PrimeNgTable } from 'primeng/table';
import { FilterMetadata, SortMeta } from 'primeng/api';

import { Subscription } from 'rxjs';

import { DateTime } from 'luxon';

import { TableFieldType } from 'dspx-core';

import { SESSION_STORAGE_KEY_PREFIXES } from '../constants/session-storage.constants';

import { AppDataService } from '../services/app-data.service';

const PrimeNgTableGlobalFilterKey = 'global';
const PrimeNgTableDateMatchModes = ['dateIs', 'dateIsNot', 'dateBefore', 'dateAfter'];

type PrimeNgTableFilters = { [s: string]: FilterMetadata | FilterMetadata[]; };

type PrimeNgTableConfiguration = {

  // Sorting
  multiSortMeta: SortMeta[];

  // Filtering
  columnFilters: PrimeNgTableFilters;

  // Paging
  first: number;
  rows: number;
};

export class GridDataClientHandler {

  private getPrimeNgTable: () => PrimeNgTable;
  private getPrimeNgTableAsync: () => Promise<PrimeNgTable>;
  private getFromSessionStorage: <TKey, TValue>(key: TKey) => TValue;
  private setIntoSessionStorage: <TKey, TValue>(key: TKey, value: TValue) => void;
  private getSessionStorageKey: () => string;

  private filterSubscription: Subscription;
  private sortSubscription: Subscription;
  private pageSubscription: Subscription;

  constructor(
    appDataService: AppDataService,
    getGridQuestionConfig: () => FormlyFieldConfig,
    getUserStorageKey: () => string,
    getGridStorageKey: () => string) {

    this.getPrimeNgTable = () => GridDataClientHandler.getPrimeNgTable(getGridQuestionConfig);
    this.getPrimeNgTableAsync = async () => this.getPrimeNgTable() ?? await GridDataClientHandler.setTimeout(() => this.getPrimeNgTable());

    this.getFromSessionStorage = key => appDataService.getFromSessionStorage(key);
    this.setIntoSessionStorage = (key, value) => appDataService.setIntoSessionStorage(key, value);

    this.getSessionStorageKey = () => `${SESSION_STORAGE_KEY_PREFIXES.gridClient}-${getUserStorageKey()}-${getGridStorageKey()}`;
  }

  async subscribe(): Promise<boolean> {

    const primeNgTable = await this.getPrimeNgTableAsync();
    if (!primeNgTable) {
      return false;
    }

    if (!this.filterSubscription) {
      this.filterSubscription = primeNgTable.onFilter.subscribe((event: any) => {
        this.copyConfigurationFromGridIntoStore();
      });
    }

    if (!this.sortSubscription) {
      this.sortSubscription = primeNgTable.onSort.subscribe((event: any) => {
        this.copyConfigurationFromGridIntoStore();
      });
    }

    if (!this.pageSubscription) {
      this.pageSubscription = primeNgTable.onPage.subscribe((event: any) => {
        this.copyConfigurationFromGridIntoStore();
      });
    }

    return true;
  }

  unsubscribe(): boolean {

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.sortSubscription) {
      this.sortSubscription.unsubscribe();
    }

    if (this.pageSubscription) {
      this.pageSubscription.unsubscribe();
    }

    return true;
  }

  async copyConfigurationFromStoreIntoGrid(): Promise<boolean> {

    const primeNgTable = await this.getPrimeNgTableAsync();
    if (!primeNgTable) {
      return false;
    }

    const configuration = this.getConfigurationFromStore();
    const success = await GridDataClientHandler.setPrimeNgTableConfiguration(primeNgTable, configuration);
    return success;
  }

  copyConfigurationFromGridIntoStore(): boolean {

    const primeNgTable = this.getPrimeNgTable();
    if (!primeNgTable) {
      return false;
    }

    const configuration = GridDataClientHandler.getPrimeNgTableConfiguration(primeNgTable);
    this.setConfigurationIntoStore(configuration);
    return true;
  }

  private getConfigurationFromStore(): PrimeNgTableConfiguration {
    const sessionStorageKey = this.getSessionStorageKey();
    const configuration = this.getFromSessionStorage<string, PrimeNgTableConfiguration>(sessionStorageKey);
    return configuration;
  }

  private setConfigurationIntoStore(configuration: PrimeNgTableConfiguration): boolean {
    const sessionStorageKey = this.getSessionStorageKey();
    this.setIntoSessionStorage(sessionStorageKey, configuration);
    return true;
  }

  private static getPrimeNgTable(getGridQuestionConfig: () => FormlyFieldConfig): PrimeNgTable {

    const gridQuestionConfig = getGridQuestionConfig();
    const formlyTableFieldType: TableFieldType = gridQuestionConfig?.props?.componentInstance;
    const primeNgTable = formlyTableFieldType?.tableInstance;

    return primeNgTable;
  }

  private static getPrimeNgTableConfiguration(primeNgTable: PrimeNgTable): PrimeNgTableConfiguration {

    if (!primeNgTable) {
      return null;
    }

    const configuration: PrimeNgTableConfiguration = {

      // Sorting
      multiSortMeta: primeNgTable.multiSortMeta,

      // Filtering
      columnFilters: this.getColumnFilters(primeNgTable.filters),

      // Paging
      first: primeNgTable.first,
      rows: primeNgTable.rows,
    };

    return configuration;
  }

  private static async setPrimeNgTableConfiguration(primeNgTable: PrimeNgTable, configuration: PrimeNgTableConfiguration): Promise<boolean> {

    if (!primeNgTable) {
      return false;
    }

    if (!configuration) {
      return false;
    }

    // Sorting
    await this.setTimeout(() => {
      primeNgTable.multiSortMeta = configuration.multiSortMeta;
      primeNgTable.sortMultiple();
      primeNgTable.cd.detectChanges();
    });

    // Filtering
    await this.setTimeout(() => {
      primeNgTable.filters = this.setColumnFilters(primeNgTable.filters, configuration.columnFilters);
      primeNgTable._filter();
      primeNgTable.cd.detectChanges();
    });

    // Paging
    await this.setTimeout(() => {
      primeNgTable.first = configuration.first;
      primeNgTable.rows = configuration.rows;
      primeNgTable.onPageChange({ first: configuration.first, rows: configuration.rows }); // This is needed to force the page change after re-sorting
      primeNgTable.cd.detectChanges();
    });

    return true;
  }

  // We are currently not supporting the "stickiness" of the global filter, because it would require setting the value in the textbox inside TableFieldType, which is tricky.
  // Therefore, when saving the filters into session store, we ignore the global filter.
  private static getColumnFilters(filters: PrimeNgTableFilters): PrimeNgTableFilters {
    const columnFilters = !filters
      ? filters
      : Object.fromEntries(Object.entries(filters).filter(([key]) => key !== PrimeNgTableGlobalFilterKey))
    return columnFilters;
  }

  // We are currently not supporting the "stickiness" of the global filter, because it would require setting the value in the textbox inside TableFieldType, which is tricky.
  // Therefore, when loading the filters from session store, we ignore the global filter.
  private static setColumnFilters(filters: PrimeNgTableFilters, columnFilters: PrimeNgTableFilters): PrimeNgTableFilters {
    const updatedFilters = !filters?.[PrimeNgTableGlobalFilterKey]
      ? { ...columnFilters }
      : { ...columnFilters, [PrimeNgTableGlobalFilterKey]: filters[PrimeNgTableGlobalFilterKey] };
    this.fixColumnFilterDateMatches(updatedFilters);
    return updatedFilters;
  }

  private static fixColumnFilterDateMatches(filters: PrimeNgTableFilters): void {
    // Due to the transformation of date columns from string to Date object happening after loading the data from the server,
    // the value of the filter match may change from Date object back to string after a server reload.
    // This method fixes the issue by converting back again from string to Date object, if required.
    const dateMatchesToFix = Object.values(filters)
      .filter(filter => Array.isArray(filter))
      .flat()
      .filter(match => PrimeNgTableDateMatchModes.includes(match.matchMode))
      .filter(dateMatch => typeof dateMatch.value === 'string');
    for (const dateMatch of dateMatchesToFix) {
      const dateTime = DateTime.fromISO(dateMatch.value);
      if (dateTime.isValid) {
        dateMatch.value = dateTime.toJSDate();
      }
    }
  }

  private static async setTimeout<TResult = void>(func: () => TResult, timeout?: number): Promise<TResult> {
    return new Promise(resolve => setTimeout(() => resolve(func()), timeout));
  }
}
